/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { Fragment, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useLocation } from 'react-router-dom'

import type { EuiThemeComputed } from '@elastic/eui'
import {
  EuiIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useEuiTheme,
} from '@elastic/eui'

import { CuiAlert } from '@modules/cui/Alert'
import { logoutUrl } from '@modules/auth/urls'
import { useAcceptEulaMutation } from '@modules/eula/hooks'

import SpinButton from '../SpinButton'
import LanguageSwitcher from '../LanguageSwitcher'

import EulaAgreement from './EulaAgreement'

const eulaStyles = (theme: EuiThemeComputed) =>
  css({
    '.eula': {
      padding: theme.size.m,
      margin: '0 auto',
      maxWidth: '50rem',
    },
  })

const eulaLogoutStyles = (theme: EuiThemeComputed) =>
  css({
    '.eula': {
      marginLeft: theme.size.m,
      color: 'inherit',
    },
  })

const Eula: React.FunctionComponent = () => {
  const intl = useIntl()
  const [lang, setLang] = useState(intl.locale)
  const { euiTheme } = useEuiTheme()
  const { pathname } = useLocation()

  const acceptEulaMutation = useAcceptEulaMutation()

  return (
    <div css={eulaStyles(euiTheme)}>
      <EuiFlexGroup gutterSize='m' alignItems='center' justifyContent='spaceBetween'>
        <EuiFlexItem grow={false}>
          <div>
            <EuiFlexGroup gutterSize='m' alignItems='center' responsive={false}>
              <EuiFlexItem grow={false}>
                <EuiIcon type='logoElastic' size='xl' />
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <EuiTitle data-test-id='eula-title-elastic'>
                  <h2>
                    <FormattedMessage id='eula.ece-title' defaultMessage='Elastic' />
                  </h2>
                </EuiTitle>
              </EuiFlexItem>
            </EuiFlexGroup>
          </div>
        </EuiFlexItem>

        <EuiFlexItem grow={false}>
          <div>
            <LanguageSwitcher lang={lang} onChange={setLang} />
          </div>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size='l' />

      <EuiText>
        <FormattedMessage
          id='eula.accept-before-use'
          defaultMessage='You need to accept our End User License Agreement before you can use Elastic Cloud Enterprise.'
        />
      </EuiText>

      <EuiSpacer size='m' />

      <EulaAgreement lang={lang} />

      <EuiSpacer size='m' />

      <EuiFlexGroup gutterSize='m' alignItems='center' justifyContent='center' responsive={false}>
        <EuiFlexItem grow={false}>
          <SpinButton
            data-test-id='eula-agree'
            color='primary'
            fill={true}
            onClick={() => acceptEulaMutation.mutate()}
            spin={acceptEulaMutation.isLoading}
          >
            <FormattedMessage id='eula.agree' defaultMessage='Agree' />
          </SpinButton>
        </EuiFlexItem>

        <EuiFlexItem grow={false}>
          <Link
            css={eulaLogoutStyles(euiTheme)}
            to={{ pathname: logoutUrl(), state: { from: pathname } }}
          >
            <FormattedMessage id='eula.decline' defaultMessage='Decline' />
          </Link>
        </EuiFlexItem>
      </EuiFlexGroup>

      {acceptEulaMutation.isError && (
        <Fragment>
          <EuiSpacer size='m' />
          <CuiAlert type='error'>{acceptEulaMutation.error}</CuiAlert>
        </Fragment>
      )}
    </div>
  )
}

export default Eula
